import React, { useState, useEffect } from "react";
import { Card, List, Avatar, Badge, Typography, Spin, Popover } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { Info } from "@mui/icons-material";
import styled from "styled-components";
import { CALL_FEATCH_API, CALL_API } from "common/API";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";

const { Text, Title } = Typography;

const ListWrapper = styled.div`
  max-height: 340px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 4px;
  }
`;

const AnnouncementItem = styled(List.Item)`
  padding: 10px 4px !important;
  border-bottom: none !important;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
    border-radius: 8px;
  }

  .ant-list-item-meta-title {
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    margin-bottom: 2px !important;
  }

  .time {
    color: #6b7280;
    font-size: 13px;
  }
`;

const ItemAction = styled.div`
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
  ${AnnouncementItem}:hover & {
    opacity: 1;
  }
`;

const formatDate = (isoString) => {
	if (!isoString) return "";
	const date = new Date(isoString);
	const day = date.getDate().toString().padStart(2, "0");
	const month = date.toLocaleDateString("en-US", { month: "short" });
	const year = date.getFullYear();
	const time = date.toLocaleTimeString("en-US", {
		hour: "2-digit",
		minute: "2-digit",
	});
	return `${day}th ${month}, ${year} ; ${time}`;
};

const AnnouncementList = () => {
	const [announcements, setAnnouncements] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [viewedIds, setViewedIds] = useState([]);

	const userInfo = useSelector(getUserInfo);
	const userList = useSelector(getUserList);
	
	const isUserReady = !!userInfo?.userid;

	const filteredAnnouncements = isUserReady
		? announcements?.filter((a) => {
		if (String(a?.createdby) === String(userInfo?.userid)) return false;

		// Role filter
		let announcementRoleIds = a?.roleid || a?.roleId || [];
		if (typeof announcementRoleIds === "string") {
			try { announcementRoleIds = JSON.parse(announcementRoleIds); } catch (e) { announcementRoleIds = []; }
		}
		if (Array.isArray(announcementRoleIds) && announcementRoleIds?.length > 0) {
			const userRoles = (userInfo?.role_id || []).map(String);
			const hasAccess = announcementRoleIds.map(String).some((id) => userRoles.includes(id));
			if (!hasAccess) return false;
		}

		// Location filter
		let announcementLocationIds = a?.locationid || a?.locationId || [];
		if (typeof announcementLocationIds === "string") {
			try { announcementLocationIds = JSON.parse(announcementLocationIds); } catch (e) { announcementLocationIds = []; }
		}
		if (Array.isArray(announcementLocationIds) && announcementLocationIds?.length > 0) {
			const userBaseLocationId = userList?.find(
			(u) => String(u.userid) === String(userInfo?.userid)
			)?.baselocationid;
			const normalizedUserLocationId = Array.isArray(userBaseLocationId)
			? userBaseLocationId.map(String)
			: [String(userBaseLocationId || "")];
			const hasLocationAccess = announcementLocationIds
			.map(String)
			.some((id) => normalizedUserLocationId.includes(id));
			if (!hasLocationAccess) return false;
		}

		return true;
		})
	: [];

	const unreadCount = filteredAnnouncements?.filter((a) => {
    const alreadyViewed =
			a.vieweduserid?.includes(userInfo?.userid) || viewedIds.includes(a.id);
		return !alreadyViewed;
	})?.length;

	const history = useHistory();

	useEffect(() => {
		fetchAnnouncements();
	}, []);

	const fetchAnnouncements = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await CALL_FEATCH_API(
				`insyts-user-dashboard/getannouncement`,
				"get"
			);
			if (response?.status === "SUCCESS") {
				const data = response?.response?.data ?? [];
				setAnnouncements(data);
			} else {
				throw new Error("API returned non-success status");
			}
		} catch (err) {
			console.error("Failed to fetch announcements:", err);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const markAsViewed = async (item) => {
		if (viewedIds.includes(item?.id)) return;
		setViewedIds((prev) => [...prev, item?.id]);
		try {
			await CALL_API(
				"insyts-user-dashboard/updateannouncement",
				"patch",
				{ id: item?.id, viewStatus: true, viewedUserId: userInfo?.userid }
			);
		} catch (err) {
			console.error("Failed to mark announcement as viewed:", err);
			setViewedIds((prev) => prev.filter((id) => id !== item?.id));
		}
	};

	const handleItemClick = (item) => {
		markAsViewed(item);
		history.push(
			`/announcement-details/${item?.id}`,
			{ viewOnly: true }
		);
	};

	const handleViewAll = () => {
		history.push("/announcement");
	};

	const getAvatarInfo = (item, index) => {
		const matchedUser = userList?.find(
			(u) => String(u.userid) === String(item?.createdby)
		);

		if (matchedUser?.profileurl) {
			return { type: "src", value: matchedUser.profileurl };
		}

		if (matchedUser?.fullname) {
			return {
			type: "letter",
			value: matchedUser.fullname.charAt(0).toUpperCase(),
			};
		}

		return {
			type: "letter",
			value: "",
		};
	};

	return (
		<>
			{/* Header */}
			<div
				className="header"
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<div style={{ display: "flex", alignItems: "center" }}>
					<Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
						Announcement
					</Title>
					<Badge
						count={unreadCount}
						style={{ backgroundColor: "#ef4444", marginLeft: 6 }}
					/>
				</div>

				<p className="view-all" style={{
					color: "#57595c",
					fontSize: "13px",
					cursor: "pointer",
					textDecoration: "underline",
				}} onClick={() => handleViewAll()}>
					View All
				</p>
			</div>

			{/* Content */}
			{loading || !isUserReady ? (
				<div style={{ textAlign: "center", padding: "32px 0" }}>
					<Spin />
				</div>
			) : error ? (
				<div style={{ textAlign: "center", padding: "16px 0" }}>
					<Text type="danger">Failed to load: {error}</Text>
				</div>
			) : filteredAnnouncements?.length === 0 ? (
				<div style={{ textAlign: "center", color: "#6b7280", padding: "16px 0" }}>
					<Text>No announcements found.</Text>
				</div>
			) : (
				<ListWrapper>
					<List
						itemLayout="horizontal"
						dataSource={filteredAnnouncements}
						renderItem={(item, index) => {
							const isImportant = item?.level === "Important";
							const isViewed =
								item?.vieweduserid?.includes(userInfo?.userid) || viewedIds.includes(item?.id);
							const avatarInfo = getAvatarInfo(item, index);
							return (
								<AnnouncementItem
									onClick={() => handleItemClick(item)}
									actions={[
										<ItemAction
											onClick={(e) => {
												e.stopPropagation();
												handleItemClick(item);
											}}
										>
											{isImportant ? (
												<Popover
													content="This is an important announcement and cannot be removed from the list."
													overlayStyle={{ maxWidth: 200 }}
													placement="bottom"
												>
													<Info
														sx={{
															color: "#2D53DA",
															fontSize: "18px",
															cursor: "pointer",
														}}
													/>
												</Popover>
											) : (
												<CloseOutlined
													style={{
														color: "#57595c",
														fontSize: "13px",
														cursor: "pointer",
													}}
												/>
											)}
										</ItemAction>,
									]}
								>
									<List.Item.Meta
										avatar={
											<Badge
												dot={!isViewed}
												offset={[-4, 4]}
												color="#ef4444"
											>
												<Avatar
													src={avatarInfo.type === "src" ? avatarInfo.value : undefined}
													size={46}
													style={{
														border: avatarInfo.type === "src" ? "2px solid #f3f4f6" : undefined,
														backgroundColor: avatarInfo.type === "letter" ? "#CCCCCC" : undefined,
														color: avatarInfo.type === "letter" ? "#FFFFFF" : undefined,
													}}
													>
													{avatarInfo.type === "letter" ? avatarInfo.value : null}
												</Avatar>
											</Badge>
										}
										title={
											<span
												style={{
													display: "block",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													maxWidth: "calc(100% - 40px)",
												}}
												title={item.title}
											>
												{item.title}
											</span>
										}
										description={
											<span className="time">
												{formatDate(item.createdat)}
											</span>
										}
									/>
								</AnnouncementItem>
							);
						}}
					/>
				</ListWrapper>
			)}
		</>
	);
};

export default AnnouncementList;